import {
  VARIANT_CONFIGS,
  VARIANT_SPECS,
  DRAW_COUNTDOWN_AUTO_MOVES,
  FRISIAN_TWO_VS_ONE_PLY_LIMIT,
  type GameVariant,
} from '@draughts/shared';

/**
 * Quanto manca alla patta automatica — da DUE regole diverse, mai attive insieme.
 *
 * 1. LIMITE DI MOSSE (italiana, internazionale, russa...): la partita finisce
 *    patta dopo N semi-mosse senza catture e senza mosse di pedina. Il contatore
 *    e' `BoardState.halfMoveClock`, che il core azzera su cattura O su mossa di
 *    pedina e incrementa solo sulla mossa di dama tranquilla (`nextHalfMoveClock`
 *    in @draughts/core); il limite e' `drawMoveLimit` della variante, dove `0`
 *    vuol dire «regola assente».
 *
 *    ⚠️ NON e' «quando rimangono solo dame», benche' i commenti del repo e
 *    GAME-RULES.md usino quella scorciatoia: il regolamento FID dice «40 mosse
 *    consecutive per giocatore senza movimento pedine E senza catture»
 *    (docs/Regole_Essenziali_Dama_Italiana.pdf §5.2), e il contatore puo' stare a
 *    39/40 con le pedine ancora in campo, semplicemente perche' nessuno le ha
 *    mosse. E' il caso in cui la patta arriva a sorpresa, e per questo l'innesco
 *    automatico guarda il contatore e non i pezzi.
 *
 * 2. DUE DAME CONTRO UNA (frisone, Frysk!): il lato forte deve vincere entro 7
 *    mosse (art. 18). Il contatore e' `BoardState.frisianEndgamePly`, il ply in
 *    cui la configurazione e' nata; il core lo azzera a `null` appena si rompe.
 *
 * Qui non si ricalcola niente: si leggono i contatori che il core mantiene gia'.
 *
 * Le due non possono convivere, e non e' una speranza: nessuna delle 27 varianti
 * della matrice ha `drawMoveLimit > 0` e `frisianEndgameDraws` insieme (frisone e
 * Frysk hanno `drawMoveLimit: 0`), e `variants-spec.test.ts` sorveglia
 * l'invariante. Per questo una riga sola basta a disegnarle entrambe.
 */
export type DrawCountdownKind = 'moveLimit' | 'frisian2v1';

export interface DrawCountdown {
  /** Semi-mosse gia' consumate, mai oltre il limite. */
  current: number;
  /** Semi-mosse totali prima della patta. Sempre > 0. */
  limit: number;
  /**
   * QUALE delle due regole sta scadendo. Serve a una cosa sola — scegliere la
   * frase dell'`aria-label`, perche' le cifre da sole non dicono quale patta sia.
   * NON serve all'innesco, che da `isDrawCountdownUrgent` in giu' e' uno per
   * tutte le varianti.
   */
  kind: DrawCountdownKind;
}

/**
 * `null` quando non c'e' niente da mostrare: variante ignota, oppure variante
 * senza regola delle N mosse (Frisone, Turca, Spagnola...).
 */
export function drawCountdownFor(
  variant: GameVariant | undefined,
  halfMoveClock: number | undefined
): DrawCountdown | null {
  if (!variant) return null;
  const limit = VARIANT_CONFIGS[variant]?.drawMoveLimit ?? 0;
  if (limit <= 0) return null;
  const current = Math.max(0, Math.min(halfMoveClock ?? 0, limit));
  return { current, limit, kind: 'moveLimit' };
}

/**
 * Il conteggio frisone delle 7 mosse. `null` fuori dal frisone, e `null` finche'
 * la configurazione 2-dame-contro-1 non e' in piedi.
 *
 * `moveCount` e `frisianEndgamePly` sono entrambi in unita' di
 * `moveHistory.length`, quindi la differenza cancella le aperture sorteggiate,
 * che partono con lo storico gia' pieno.
 *
 * ⚠️ Legge `VARIANT_SPECS` e non `VARIANT_CONFIGS`: `BoardConfig` porta
 * `drawMoveLimit` ma non `frisianEndgameDraws`. Stessa fonte che usa la pagina
 * Regole (`deriveRules.ts`).
 */
export function frisianCountdownFor(
  variant: GameVariant | undefined,
  frisianEndgamePly: number | null | undefined,
  moveCount: number | undefined
): DrawCountdown | null {
  if (!variant) return null;
  if (!VARIANT_SPECS[variant]?.rules.frisianEndgameDraws) return null;
  // `== null` e non `!`: lo zero e' un ply legittimo, e un `!` lo scarterebbe.
  if (frisianEndgamePly == null) return null;
  const limit = FRISIAN_TWO_VS_ONE_PLY_LIMIT;
  const trascorsi = (moveCount ?? 0) - frisianEndgamePly;
  const current = Math.max(0, Math.min(trascorsi, limit));
  return { current, limit, kind: 'frisian2v1' };
}

/**
 * Le mosse COMPLETE, che sono il modo in cui le regole si enunciano (e in cui la
 * pagina Regole gia' le scrive: `drawMoveLimit / 2`). Il riempimento della barra
 * resta invece in semi-mosse, cosi' avanza a ogni singola mossa di dama.
 */
export function drawCountdownMoves({ current, limit }: DrawCountdown): {
  done: number;
  total: number;
  remaining: number;
} {
  const total = Math.round(limit / 2);
  const done = Math.min(Math.floor(current / 2), total);
  return { done, total, remaining: total - done };
}

/** Ultimo tratto: la barra passa all'ambra. */
export const DRAW_COUNTDOWN_NEAR = 0.8;

export function isDrawCountdownNear({ current, limit }: DrawCountdown): boolean {
  return current >= limit * DRAW_COUNTDOWN_NEAR;
}

/**
 * L'INNESCO AUTOMATICO: la scadenza e' abbastanza vicina da farsi vedere senza
 * che nessuno abbia chiesto patta.
 *
 * Una regola per tutte le varianti, e il frisone ne esce gratis: ha 7 mosse in
 * tutto, `7 <= 10`, quindi il conteggio delle due dame contro una e' sempre
 * visibile per conseguenza — nessun ramo dedicato.
 *
 * Non puo' lampeggiare: il contatore sale di uno o si azzera di netto, quindi
 * attraversa la soglia una volta in salita; la mossa di pedina lo riporta a zero
 * e la riga se ne va, che e' onesto — la scadenza si e' davvero allontanata.
 */
export function isDrawCountdownUrgent(value: DrawCountdown): boolean {
  return drawCountdownMoves(value).remaining <= DRAW_COUNTDOWN_AUTO_MOVES;
}
